// frontend/src/widgets/RoleStatsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-16
// Updated:      2026-09-09 15:06 BST
//
// Widget to fetch and display role statistics in consistent layout with other widgets.
//
// Theme update:
//   - Stat card borders use tenant accent colour.
//   - Role/stat headers use tenant accent colour.
//
// AUD8 tenant fix:
//   - Does not trust /api/roles/ user_count because that endpoint may return global counts.
//   - Fetches tenant-scoped /api/users/ and calculates counts from the logged-in user's tenant context.
//
// Mobile Responsiveness (2026-09-09):
//   - Desktop retains the existing three-column role-stat grid.
//   - Mobile converts the same role-stat cards into a single-column vertical stream.
//   - Mobile shows one complete role-stat card per internal vertical scroll/snap.
//   - Dynamic mobile height uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary viewport/root sizing;
//       measure the natural first role-stat card;
//       size the mobile records viewport to one complete card plus its padding;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - ResizeObserver watches the first rendered role-stat card so content/font changes
//     cannot leave the widget clipped.
//   - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//   - Existing role/user loading and AUD8 tenant-scoped counting behaviour is unchanged.
//   - Existing direct axios/auth-header behaviour is intentionally preserved in this
//     mobile pass; no API/auth refactor is mixed into the responsive change.

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";

const ROLE_ORDER = [
  "Super Admin",
  "System Admin",
  "System Manager",
  "Operator",
  "User",
  "Client",
  "Guest",
];

const RoleStatsWidget = () => {
  const { theme } = useTheme();
  const { user, token } = useAuth();

  const widgetRootRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstRoleCardRef = useRef(null);

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const accentColor = theme?.accent_color || "#ff6600";

  useEffect(() => {
    let cancelled = false;

    const jwt =
      token ||
      localStorage.getItem("anpr_token") ||
      sessionStorage.getItem("anpr_token") ||
      localStorage.getItem("token") ||
      "";

    const headers = jwt
      ? {
          Authorization: `Bearer ${jwt.replace(/^Bearer\s+/i, "")}`,
        }
      : {};

    const loadStats = async () => {
      setLoading(true);
      setError(null);

      try {
        const [rolesRes, usersRes] = await Promise.all([
          axios.get("/api/roles/", { headers }),
          axios.get("/api/users/?limit=1000", { headers }),
        ]);

        if (cancelled) return;

        setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      } catch (err) {
        if (cancelled) return;

        console.error(
          "Failed to load role statistics:",
          err.response?.data || err
        );

        setRoles([]);
        setUsers([]);
        setError("Failed to load role statistics");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStats();

    return () => {
      cancelled = true;
    };
  }, [token, user?.tenant_id, user?.id]);

  const roleStats = useMemo(() => {
    const counts = users.reduce((acc, currentUser) => {
      const roleName = currentUser?.role || "User";
      acc[roleName] = (acc[roleName] || 0) + 1;
      return acc;
    }, {});

    const knownRoles = roles.length
      ? roles.map((role) => ({
          id: role.id,
          name: role.name,
          user_count: counts[role.name] || 0,
        }))
      : ROLE_ORDER.map((name, index) => ({
          id: index + 1,
          name,
          user_count: counts[name] || 0,
        }));

    return knownRoles.sort((a, b) => {
      const aIndex = ROLE_ORDER.indexOf(a.name);
      const bIndex = ROLE_ORDER.indexOf(b.name);

      if (aIndex === -1 && bIndex === -1) {
        return a.name.localeCompare(b.name);
      }

      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });
  }, [roles, users]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const viewport = recordsViewportRef.current;
    const firstCard = firstRoleCardRef.current;

    if (!root || !viewport) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstCard = firstRoleCardRef.current;

        if (currentFirstCard) {
          const cardHeight = Math.ceil(
            Math.max(
              currentFirstCard.getBoundingClientRect().height || 0,
              currentFirstCard.scrollHeight || 0
            )
          );

          if (Number.isFinite(cardHeight) && cardHeight > 0) {
            const viewportHeight = cardHeight + 32;

            viewport.style.height = `${viewportHeight}px`;
            viewport.style.minHeight = `${viewportHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstCard) {
      resizeObserver.observe(firstCard);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    accentColor,
    error,
    loading,
    roleStats.length,
  ]);

  if (loading) {
    return <p>Loading roles...</p>;
  }

  if (error) {
    return <p className="text-red-600">{error}</p>;
  }

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible rounded-lg border border-gray-200 shadow-sm bg-white md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-role-stats-viewport {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
            scroll-padding-top: 16px;
          }

          .vx-role-stats-viewport::-webkit-scrollbar {
            display: none;
          }

          .vx-role-stat-card {
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }
        }
      `}</style>

      <div
        ref={recordsViewportRef}
        className="vx-role-stats-viewport h-auto w-full overflow-visible md:h-full md:overflow-auto"
      >
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
          {roleStats.length === 0 ? (
            <p className="text-center text-gray-500 md:col-span-3">
              No roles found.
            </p>
          ) : (
            roleStats.map((role, index) => (
              <div
                key={role.id || role.name}
                ref={index === 0 ? firstRoleCardRef : null}
                className="vx-role-stat-card flex min-w-0 flex-col items-center justify-center rounded px-4 py-3"
                style={{
                  border: `1px solid ${accentColor}`,
                }}
              >
                <div
                  className="break-words text-center text-lg font-semibold"
                  style={{
                    color: accentColor,
                    overflowWrap: "anywhere",
                  }}
                >
                  {role.name}
                </div>

                <div className="text-sm text-gray-600">
                  {role.user_count ?? 0} Users
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleStatsWidget;
